import AbstractOptionsModal from 'components/pages/login/layout/AbstractOptionsModal';
import { AppData, Registry } from '@lightningjs/sdk';
import { translate } from 'support/translate';
import Button from 'components/common/buttons/Button';
import { formatVideoTime, getVideoTimeTts } from 'support/contentUtils';
import { debounceAnnounce } from 'support/generalUtils';
import { LoginFlow } from '../layout/AbstractLoginModal';

const DEFAULT_RESEND_COUNTDOWN_SEC = 60;

const RESEND_REF = 'ResendCta';
const GO_BACK_REF = 'GoBackRef';

export enum ConfirmationType {
  SIGN_UP = 'SignUp',
  SIGN_IN = 'SignIn',
  FORGOT_PASSWORD = 'ForgotPassword',
}

type ResendBackoffState = {
  email: string;
  confirmationType: ConfirmationType;
  retries: number;
  retryAllowedTimestamp: number;
};

export default class EmailConfirmationModal extends AbstractOptionsModal {
  override _showBackButton = false;
  override _showSkipButton = true;

  override get loginFlow() {
    return this._confirmationType === ConfirmationType.SIGN_UP
      ? LoginFlow.SIGN_UP
      : LoginFlow.SIGN_IN;
  }

  private _ResendButton: Button | null = null;

  private _email = '';
  private _baseResendSeconds = 0;
  private _retries = 0;
  private _retryAllowedTimestamp = 0;
  private _resendEnableTimer: number | null = null;
  private _confirmationType: ConfirmationType | null = null;
  private _resendButtonIndex = 0;
  private _goBackButtonIndex = 1;

  private _sessionState: ResendBackoffState | null = null;

  override _init() {
    super._init();

    this.checkmarkIconVisible = true;
    this.headingText = translate('account.emailConfirmation.heading');

    this.optionItems = [
      {
        ref: RESEND_REF,
        label: translate('account.emailConfirmation.resendCta'),
        action: '$resendLink',
        passSignals: { $resendLink: '$resendLink' },
      },
      {
        ref: GO_BACK_REF,
        label: translate('account.emailConfirmation.returnToSignIn'),
        action: '$goBack',
        passSignals: { $goBack: '$goBack' },
      },
    ];
    this.optionSignals = {
      $resendLink: true,
      $goBack: true,
    };

    this._resendButtonIndex = this._OptionsList.items.findIndex(
      item => item.ref === RESEND_REF,
    );
    this._goBackButtonIndex = this._OptionsList.items.findIndex(
      item => item.ref === GO_BACK_REF,
    );

    this._ResendButton = this._OptionsList.getItemByRef(RESEND_REF)! as Button;
  }

  modalInit(data: { email: string; confirmationType: ConfirmationType }) {
    this._email = data.email;
    this.subHeadingText = translate(
      'account.emailConfirmation.subheading',
      this._email,
    );

    this._confirmationType = data.confirmationType;
    this._baseResendSeconds =
      AppData?.ux?.resend_link_timer_seconds ?? DEFAULT_RESEND_COUNTDOWN_SEC;
  }

  override _enable() {
    super._enable();

    if (!this._resendEnableTimer) this.disableResendButton();

    if (
      this._confirmationType === ConfirmationType.SIGN_IN ||
      this._confirmationType === ConfirmationType.SIGN_UP
    ) {
      AppData?.smartLoginService?.startPoll(this.pollConfirmation.bind(this));
    }
  }

  override _inactive() {
    super._inactive();
    this.clearTimers();
    AppData?.smartLoginService?.reset();
  }

  private disableResendButton() {
    if (
      this._sessionState &&
      this._sessionState.email === this._email &&
      this._sessionState.confirmationType === this._confirmationType
    ) {
      // Restore countdown
      this._retries = this._sessionState.retries;
      this._retryAllowedTimestamp = this._sessionState.retryAllowedTimestamp;
      this._sessionState = null;
    }

    const countdown = this.getCountdown();
    if (countdown <= 0) this.setNextExponentialCountdown();

    this.updateResendButton();
    this._OptionsList.patch({
      index: this._goBackButtonIndex,
      unfocusableIndices: [this._resendButtonIndex],
    });

    const resendButtonTTS =
      countdown > 0
        ? translate(
            'account.emailConfirmation.requestNewLinkIn',
            getVideoTimeTts(formatVideoTime(countdown)),
          )
        : translate('account.emailConfirmation.resendCta');
    debounceAnnounce(this, resendButtonTTS ?? '');

    this._resendEnableTimer = Registry.setInterval(() => {
      this.updateResendButton();
    }, 1000);
  }

  private enableButton() {
    this.clearResendTimer();
    this._OptionsList.patch({ unfocusableIndices: [] });
  }

  private updateResendButton() {
    const countdown = Math.round(this.getCountdown());
    let label = translate('account.emailConfirmation.resendCta');
    const disabled = countdown > 0;

    if (disabled) {
      label = translate(
        'account.emailConfirmation.requestNewLinkIn',
        formatVideoTime(countdown),
      );
    } else {
      this.enableButton();
    }

    this._ResendButton?.patch({
      disabled,
      label,
    });
  }

  private pollConfirmation() {
    AppData?.modalService.open('SignInConfirmationModal');
  }

  private getCountdown() {
    if (!this._retryAllowedTimestamp) return 0;
    return (this._retryAllowedTimestamp - Date.now()) / 1000;
  }

  private setNextExponentialCountdown() {
    const newSeconds = this._baseResendSeconds * 2 ** this._retries;
    this._retryAllowedTimestamp = Date.now() + newSeconds * 1000;
    this._retries++;
  }

  private clearTimers() {
    this.clearResendTimer();

    this._sessionState = {
      email: this._email,
      confirmationType: this._confirmationType!,
      retries: this._retries,
      retryAllowedTimestamp: this._retryAllowedTimestamp,
    };

    this._retries = 0;
    this._retryAllowedTimestamp = 0;
  }

  private clearResendTimer() {
    if (this._resendEnableTimer) {
      Registry.clearInterval(this._resendEnableTimer);
      this._resendEnableTimer = null;
    }
  }

  private async $resendLink() {
    this.disableResendButton();

    switch (this._confirmationType) {
      case ConfirmationType.SIGN_UP:
      case ConfirmationType.SIGN_IN: {
        const smartLoginService = AppData?.smartLoginService;
        if (!smartLoginService) return;

        await this.onLoading(smartLoginService.sendSignInEmail(this._email));
        break;
      }
      case ConfirmationType.FORGOT_PASSWORD: {
        await this.onLoading(
          AppData!.loginRadiusService!.forgotPassword(this._email),
        );
        break;
      }
    }
  }

  private $goBack() {
    AppData?.modalService.close();
    AppData?.modalService.open('SignInModal', { data: { email: this._email } });
  }
}
